import React, { createContext, useContext, useReducer, useEffect, useMemo, useRef, useCallback } from 'react';
import { playUserAudioPromise } from '../../utils/playUserAudioPromise';
import { useAppConfig } from '../../context/AppConfigContext';
import { Capacitor } from '@capacitor/core';

const initialState = {
  expression: 'default',
  bubbleText: '',
  currentMessage: null,
  isTyping: false,
  isInteracting: false,
  messageVariant: 'ephemeral',
  lastInteraction: Date.now(),
  isRecording: false,
  cancelTyping: false,
  bubbleSize: { fixed: false, width: 0, height: 0 },
  config: {
    blinkFrequency: 7000,
    sleepDelay: 180000,
    textTypingSpeed: 25,
    animationSpeed: {
      blink: 0.1,
      eyes: 1,
      emotions: 0.6,
    },
  },
};

const ActionTypes = {
  SET_EXPRESSION: 'SET_EXPRESSION',
  SET_BUBBLE_TEXT: 'SET_BUBBLE_TEXT',
  SET_CURRENT_MESSAGE: 'SET_CURRENT_MESSAGE',
  CLEAR_CURRENT_MESSAGE: 'CLEAR_CURRENT_MESSAGE',
  SET_MESSAGE_VARIANT: 'SET_MESSAGE_VARIANT',
  SET_TYPING: 'SET_TYPING',
  SET_INTERACTING: 'SET_INTERACTING',
  UPDATE_LAST_INTERACTION: 'UPDATE_LAST_INTERACTION',
  UPDATE_CONFIG: 'UPDATE_CONFIG',
  RESET_STATE: 'RESET_STATE',
  CANCEL_TYPING: 'CANCEL_TYPING',
  CLEAR_CANCEL_TYPING: 'CLEAR_CANCEL_TYPING',
  SET_RECORDING: 'SET_RECORDING',
  SET_BUBBLE_SIZE: 'SET_BUBBLE_SIZE',
};

const assistantReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_EXPRESSION:
      return { ...state, expression: action.payload };
    case ActionTypes.SET_BUBBLE_TEXT:
      return { ...state, bubbleText: action.payload };
    case ActionTypes.SET_CURRENT_MESSAGE:
      return {
        ...state,
        currentMessage: action.payload,
        bubbleText: '',
        isTyping: true,
        cancelTyping: false,
        lastInteraction: Date.now(),
      };
    case ActionTypes.CLEAR_CURRENT_MESSAGE:
      return { ...state, currentMessage: null, isTyping: false };
    case ActionTypes.SET_MESSAGE_VARIANT:
      return { ...state, messageVariant: action.payload };
    case ActionTypes.SET_TYPING:
      return { ...state, isTyping: action.payload };
    case ActionTypes.SET_INTERACTING:
      return { ...state, isInteracting: action.payload };
    case ActionTypes.UPDATE_LAST_INTERACTION:
      return { ...state, lastInteraction: Date.now() };
    case ActionTypes.UPDATE_CONFIG:
      return {
        ...state,
        config: { ...state.config, ...action.payload },
      };
    case ActionTypes.CANCEL_TYPING:
      return {
        ...state,
        cancelTyping: true,
      };
    case ActionTypes.CLEAR_CANCEL_TYPING:
      return {
        ...state,
        cancelTyping: false,
      };
    case ActionTypes.RESET_STATE:
      return {
        ...state,
        expression: 'default',
        bubbleText: '',
        currentMessage: null,
        isTyping: false,
        isInteracting: false,
        isRecording: false,
        cancelTyping: false,
        messageVariant: 'ephemeral',
        bubbleSize: { fixed: false, width: 0, height: 0 },
        lastInteraction: Date.now(),
      };
    case ActionTypes.SET_RECORDING:
      return { ...state, isRecording: action.payload };
    case ActionTypes.SET_BUBBLE_SIZE:
      return { ...state, bubbleSize: { ...state.bubbleSize, ...action.payload } };
    default:
      return state;
  }
};

const AssistantContext = createContext();

export const AssistantProvider = ({ children, isRecording = false }) => {
  const [state, dispatch] = useReducer(assistantReducer, initialState);
  const pendingSpeechResolversRef = useRef(new Map());
  const activeAudioAbortRef = useRef(null);
  const messageIdRef = useRef(0);
  const activeMessageIdRef = useRef(null);
  const { site } = useAppConfig();

  useEffect(() => {
    activeMessageIdRef.current = state.currentMessage?.id || null;
  }, [state.currentMessage?.id]);

  useEffect(() => {
    dispatch({ type: ActionTypes.SET_RECORDING, payload: isRecording });
  }, [isRecording]);

  const resolveSpeech = useCallback((messageId, status = 'typed') => {
    if (!messageId) return;
    const resolver = pendingSpeechResolversRef.current.get(messageId);
    if (resolver) {
      resolver({ messageId, status });
      pendingSpeechResolversRef.current.delete(messageId);
    }
  }, []);

  const clearPendingSpeech = useCallback((status = 'interrupted') => {
    pendingSpeechResolversRef.current.forEach((resolve) => resolve({ status }));
    pendingSpeechResolversRef.current.clear();
  }, []);

  const stopActiveAudio = useCallback(() => {
    if (!activeAudioAbortRef.current) return;
    activeAudioAbortRef.current.abort();
    activeAudioAbortRef.current = null;
  }, []);

  const isNative = Capacitor.isNativePlatform();

  const normalizeAudioUrl = (src) => {
    if (!src) return src;
    if (/^https?:\/\//i.test(src)) return src;
    if (!isNative) return src;
    return `${site}${src.startsWith('/') ? '' : '/'}${src}`;
  };  

  const actions = useMemo(
    () => ({
      setBubbleSize: (size) => {
        dispatch({ type: ActionTypes.SET_BUBBLE_SIZE, payload: size });
      },
      setRecording: (nextRecording) => {
        dispatch({ type: ActionTypes.SET_RECORDING, payload: nextRecording });
      },
      setExpression: (expression) => {
        dispatch({ type: ActionTypes.SET_EXPRESSION, payload: expression });
      },
      setBubbleText: (text) => {
        dispatch({ type: ActionTypes.SET_BUBBLE_TEXT, payload: text });
      },
      setTyping: (typing) => {
        dispatch({ type: ActionTypes.SET_TYPING, payload: typing });
      },
      setInteracting: (interacting) => {
        dispatch({ type: ActionTypes.SET_INTERACTING, payload: interacting });
      },
      updateLastInteraction: () => {
        dispatch({ type: ActionTypes.UPDATE_LAST_INTERACTION });
      },
      updateConfig: (configUpdates) => {
        dispatch({ type: ActionTypes.UPDATE_CONFIG, payload: configUpdates });
      },
      cancelTyping: () => {
        dispatch({ type: ActionTypes.CANCEL_TYPING });
      },
      clearCancelTyping: () => {
        dispatch({ type: ActionTypes.CLEAR_CANCEL_TYPING });
      },
      completeSpeech: (messageId, status = 'typed') => {
        resolveSpeech(messageId, status);
      },
      dismiss: (status = 'dismissed') => {
        const currentId = activeMessageIdRef.current;
        if (status !== 'auto_hidden') {
          stopActiveAudio();
        }
        dispatch({ type: ActionTypes.SET_BUBBLE_TEXT, payload: '' });
        dispatch({ type: ActionTypes.SET_BUBBLE_SIZE, payload: { fixed: false, width: 0, height: 0 } });
        dispatch({ type: ActionTypes.CLEAR_CURRENT_MESSAGE });
        dispatch({ type: ActionTypes.SET_MESSAGE_VARIANT, payload: 'ephemeral' });
        resolveSpeech(currentId, status);
      },
      resetState: () => {
        clearPendingSpeech('reset');
        stopActiveAudio();
        dispatch({ type: ActionTypes.RESET_STATE });
      },
      showSuccess: () => {
        dispatch({ type: ActionTypes.RESET_STATE });
        dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'happy' });
        setTimeout(() => {
          dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'default' });
        }, 3000);
      },
      showError: () => {
        dispatch({ type: ActionTypes.RESET_STATE });
        dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'sad' });
        setTimeout(() => {
          dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'default' });
        }, 3000);
      },
      showThinking: () => {
        dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'thinking' });
      },
      showSurprise: () => {
        dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'surprised' });
        setTimeout(() => {
          dispatch({ type: ActionTypes.SET_EXPRESSION, payload: 'default' });
        }, 2000);
      },
      speakText: (text, options = {}) => {
        if (!text || typeof text !== 'string') {
          return Promise.resolve({ status: 'skipped' });
        }

        const variant = options.variant || 'ephemeral';
        const audioTimeoutMs = options.audioTimeoutMs;
        const audioStartDelayMs = options.audioStartDelayMs ?? 350;
        const audioDebugLabel = options.audioDebugLabel || 'assistant_audio';
        const messageId = ++messageIdRef.current;

        const rawAudioSrc = options.audioSrc || null;
        const audioSrc = normalizeAudioUrl(rawAudioSrc);
        
        clearPendingSpeech('interrupted');
        stopActiveAudio();

        dispatch({ type: ActionTypes.SET_MESSAGE_VARIANT, payload: variant });
        dispatch({
          type: ActionTypes.SET_CURRENT_MESSAGE,
          payload: {
            id: messageId,
            text,
            variant,
            autoHideMs: options.autoHideMs,
          },
        });

        const typedPromise = new Promise((resolve) => {
          pendingSpeechResolversRef.current.set(messageId, resolve);
        });

        if (!audioSrc) {
          return typedPromise;
        }

        const audioAbortController = new AbortController();
        activeAudioAbortRef.current = audioAbortController;

        const audioPromise = playUserAudioPromise(audioSrc, {
          timeoutMs: audioTimeoutMs,
          debugLabel: audioDebugLabel,
          startDelayMs: audioStartDelayMs,
          signal: audioAbortController.signal,
        });

        return typedPromise.then(async (result) => {
          if (result?.status !== 'typed') {
            return result;
          }

          const audioResult = await audioPromise;
          if (activeAudioAbortRef.current === audioAbortController) {
            activeAudioAbortRef.current = null;
          }

          return { ...result, audio: audioResult };
        });
      },
    }),
    [clearPendingSpeech, resolveSpeech, stopActiveAudio]
  );

  return (
    <AssistantContext.Provider value={{ state, actions }}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within an AssistantProvider');
  }
  return context;
};
